import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateExistingProduct } from "store/features/product/productActions";
import { FiUpload, FiX, FiPlus, FiMinus } from "react-icons/fi";
import { ChromePicker } from "react-color";

const VendorEditProduct = () => {
  const { productId } = useParams();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get product
  const product = state?.product;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    discountPrice: "",
    stock: "",
    categories: "",
    sizes: "",
    gender: "men",
  });
  const [colors, setColors] = useState([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState("#000000");
  const [images, setImages] = useState({
    mainImage: null,
    gallery: [],
  });
  const [previews, setPreviews] = useState({
    mainImage: null,
    gallery: [],
  });

  useEffect(() => {
    if (product) {
      const productColors = product.colors?.[0]?.split(",") || [];
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price,
        discountPrice: product.discountPrice || "",
        stock: product.stock,
        categories: product.categories?.[0] || "",
        sizes: product.sizes?.[0]?.split(",")?.join(", ") || "",
        gender: product.gender || "men",
      });
      setColors(productColors);
      setPreviews({
        mainImage: product.mainImage?.[0] || null,
        gallery: product.gallery || [],
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;

    if (name === "mainImage") {
      setImages((prev) => ({ ...prev, [name]: files[0] }));
      setPreviews((prev) => ({
        ...prev,
        [name]: files[0] ? URL.createObjectURL(files[0]) : null,
      }));
    } else if (name === "gallery") {
      setImages((prev) => ({ ...prev, [name]: Array.from(files) }));
      setPreviews((prev) => ({
        ...prev,
        gallery: [
          ...prev.gallery,
          ...Array.from(files).map((file) => URL.createObjectURL(file)),
        ],
      }));
    }
  };

  const removeGalleryImage = (index) => {
    const isNewImage = typeof previews.gallery[index] !== "string";
    if (isNewImage) {
      const newGallery = [...images.gallery];
      newGallery.splice(index, 1);
      setImages((prev) => ({ ...prev, gallery: newGallery }));
    }
    const newPreviews = [...previews.gallery];
    newPreviews.splice(index, 1);
    setPreviews((prev) => ({ ...prev, gallery: newPreviews }));
  };

  const handleAddColor = () => {
    if (currentColor && !colors.includes(currentColor)) {
      setColors([...colors, currentColor]);
      setCurrentColor("#000000");
      setShowColorPicker(false);
    }
  };

  const handleRemoveColor = (index) => {
    const newColors = [...colors];
    newColors.splice(index, 1);
    setColors(newColors);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined) {
        if (key === "sizes") {
          const values = formData[key].split(",").map((item) => item.trim());
          data.append(key, values.join(","));
        } else if (key === "categories") {
          data.append(key, formData[key]);
        } else {
          data.append(key, formData[key]);
        }
      }
    });

    // Add colors to form data
    data.append("colors", colors.join(","));

    if (images.mainImage) data.append("mainImage", images.mainImage);
    images.gallery.forEach((file) => data.append("gallery", file));

    dispatch(updateExistingProduct(productId, data));
    navigate("/vendor/product")
  };

  if (!product)
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-indigo-600"></div>
      </div>
    );

  return (
    <div className="w-full px-4 py-8">
      <div className="mx-auto ">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800">Edit Product</h1>
          <button
            onClick={() => navigate("/vendor/product")}
            className="border-transparent inline-flex items-center rounded-md border bg-indigo-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Back
          </button>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-md">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Left Column */}
              <div>
                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Product Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    rows="4"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Category ID
                  </label>
                  <input
                    type="text"
                    name="categories"
                    value={formData.categories}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="e.g., 68287e8975d80e472943da1e"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="unisex">Unisex</option>
                    <option value="kids">Kids</option>
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div>
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 block font-medium text-gray-700">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-medium text-gray-700">
                      Discount Price (₹)
                    </label>
                    <input
                      type="number"
                      name="discountPrice"
                      value={formData.discountPrice}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Colors
                  </label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {colors.map((color, index) => (
                        <div
                          key={index}
                          className="flex items-center rounded-md border border-gray-200 px-2 py-1"
                        >
                          <div
                            className="mr-2 h-5 w-5 rounded-full border border-gray-300"
                            style={{ backgroundColor: color }}
                          ></div>
                          <span className="text-sm">{color}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveColor(index)}
                            className="ml-2 text-gray-500 hover:text-red-500"
                          >
                            <FiMinus size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        className="flex items-center rounded-md bg-indigo-600 px-3 py-1 text-sm text-white hover:bg-indigo-700"
                      >
                        <FiPlus className="mr-1" size={14} />
                        Add Color
                      </button>
                      {showColorPicker && (
                        <div className="absolute left-0 top-8 z-10">
                          <ChromePicker
                            color={currentColor}
                            onChangeComplete={(color) =>
                              setCurrentColor(color.hex)
                            }
                          />
                          <button
                            type="button"
                            onClick={handleAddColor}
                            className="mt-2 w-full rounded-md bg-indigo-600 py-1 text-white hover:bg-indigo-700"
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Sizes (comma separated)
                  </label>
                  <input
                    type="text"
                    name="sizes"
                    value={formData.sizes}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="e.g., S, M, L, XL"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Image Uploads */}
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block font-medium text-gray-700">
                  Main Image
                </label>
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-4 text-center">
                  {previews.mainImage ? (
                    <div className="relative">
                      <img
                        src={
                          typeof previews.mainImage === "string"
                            ? previews.mainImage
                            : previews.mainImage
                        }
                        alt="Main preview"
                        className="mx-auto h-48 w-full object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImages((prev) => ({ ...prev, mainImage: null }));
                          setPreviews((prev) => ({ ...prev, mainImage: null }));
                        }}
                        className="absolute right-2 top-2 rounded-full bg-white p-1 shadow-md hover:bg-gray-100"
                      >
                        <FiX className="h-5 w-5 text-red-600" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <FiUpload className="mb-2 h-12 w-12 text-gray-400" />
                      <p className="text-gray-500">Upload main product image</p>
                      <input
                        type="file"
                        name="mainImage"
                        onChange={handleFileChange}
                        className="hidden"
                        id="mainImage"
                        accept="image/*"
                      />
                      <label
                        htmlFor="mainImage"
                        className="mt-2 inline-flex cursor-pointer items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                      >
                        Select Image
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-2 block font-medium text-gray-700">
                  Gallery Images
                </label>
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-4">
                  {previews.gallery.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {previews.gallery.map((preview, index) => (
                        <div key={index} className="relative">
                          <img
                            src={
                              typeof preview === "string" ? preview : preview
                            }
                            alt={`Gallery preview ${index}`}
                            className="h-24 w-full rounded object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(index)}
                            className="absolute right-1 top-1 rounded-full bg-white p-1 shadow-md hover:bg-gray-100"
                          >
                            <FiX className="h-4 w-4 text-red-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-4">
                      <FiUpload className="mb-2 h-12 w-12 text-gray-400" />
                      <p className="text-gray-500">
                        Upload additional product images
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    name="gallery"
                    onChange={handleFileChange}
                    className="hidden"
                    id="gallery"
                    accept="image/*"
                    multiple
                  />
                  <label
                    htmlFor="gallery"
                    className="mt-2 inline-flex cursor-pointer items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                  >
                    {previews.gallery.length > 0
                      ? "Add More Images"
                      : "Select Images"}
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Update Product
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VendorEditProduct;
